import { GeoJson } from '@ais-ui/api-spec/shapes';
import { DrawGeometryType } from '@ais-ui/react-openlayers';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@ais-ui/ui-core';
import React, { useState } from 'react';
import {
  PiCircleThin,
  PiFolderThin,
  PiIntersectSquareThin,
  PiLineSegmentThin,
  PiMapPinThin,
  PiPolygonThin,
  PiUploadThin,
} from 'react-icons/pi';
import { CreateShapeGroup } from './Mutations/CreateShapeGroup';
import { useLocalization } from '@ais-ui/locale-management';
import { ImportShapeDialog } from './Mutations/ImportShape';
import { CreateShape } from './Mutations/CreateShape';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

const ShapeButton = ({
  id,
  Icon,
  onClick,
  text,
}: {
  id: string;
  Icon: React.ElementType;
  onClick: () => void;
  text: string;
}) => {
  return (
    <Button
      id={id}
      variant="ghost"
      onClick={onClick}
      className="flex gap-x-2 col-span-1 justify-start"
    >
      <Icon></Icon>
      {text}
    </Button>
  );
};

export const MapShapeMenu = ({
  buttonText,
  className,
}: {
  buttonText: string;
  className?: string;
}) => {
  const { messages } = useLocalization();
  const { viewMode } = useViewMode();
  const [showCreateShapeGroupDialog, setShowCreateShapeGroupDialog] =
    useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [geometryType, setGeometryType] = useState<DrawGeometryType | null>(
    null
  );

  // Popover should never be open during Drawing mode
  const isPopoverOpen = isOpen && viewMode !== ViewMode.Drawing;

  return (
    <div className="flex w-full justify-end">
      <Popover open={isPopoverOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button id="create-shape-button" className={className}>
            {buttonText}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" side="top" align="end">
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">Shapes</h4>
              <p className="text-sm text-muted-foreground">
                {messages.shapeCreateMenuSubtitle}
              </p>
            </div>
            <div className="grid gap-2"></div>
          </div>
          <div className="grid grid-cols-2">
            <ShapeButton
              id="draw-circle-button"
              Icon={PiCircleThin}
              onClick={() => {
                setIsOpen(false);
                setShowCreateDialog(true);
                setGeometryType('Circle');
              }}
              text={messages.shapeCreateMenuCircle}
            ></ShapeButton>
            <ShapeButton
              id="draw-linestring-button"
              Icon={PiLineSegmentThin}
              onClick={() => {
                setIsOpen(false);
                setShowCreateDialog(true);
                setGeometryType('LineString');
              }}
              text={messages.shapeCreateMenuLine}
            ></ShapeButton>
            <ShapeButton
              id="draw-point-button"
              Icon={PiMapPinThin}
              onClick={() => {
                setShowCreateDialog(true);
                setGeometryType('Point');
              }}
              text={messages.shapeCreateMenuPoint}
            ></ShapeButton>
            <ShapeButton
              id="draw-polygon-button"
              Icon={PiPolygonThin}
              onClick={() => {
                setIsOpen(false);
                setShowCreateDialog(true);
                setGeometryType('Polygon');
              }}
              text={messages.shapeCreateMenuPolygon}
            ></ShapeButton>
          </div>
          <Separator className="my-2"></Separator>
          <div className="grid grid-cols-2">
            <ShapeButton
              id="create-shape-group-button"
              Icon={PiFolderThin}
              onClick={() => setShowCreateShapeGroupDialog(true)}
              text={messages.shapeCreateMenuGroup}
            ></ShapeButton>
            <ShapeButton
              id="import-shape-button"
              Icon={PiUploadThin}
              onClick={() => setShowImportDialog(true)}
              text={messages.shapeImportMenu}
            ></ShapeButton>
          </div>
        </PopoverContent>
      </Popover>
      {showCreateDialog && geometryType && (
        <CreateShape
          setShowDialog={setShowCreateDialog}
          geometryType={geometryType}
          startInDrawMode={true}
        ></CreateShape>
      )}
      {showCreateShapeGroupDialog && (
        <CreateShapeGroup
          showDialog={showCreateShapeGroupDialog}
          setShowDialog={setShowCreateShapeGroupDialog}
        />
      )}
      {showImportDialog && (
        <ImportShapeDialog
          showDialog={showImportDialog}
          setShowDialog={setShowImportDialog}
        ></ImportShapeDialog>
      )}
    </div>
  );
};
